import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ModuleRegistryService } from '../services/modules/module-registry.service';
import { PermissionService } from '../services/permission/permission.service';
import { EnabledModule } from '../services/modules/module-registry.models';

/**
 * Resolves which enabled module (if any) owns a URL, matching on the URL's first path
 * segment (ignoring the query string and any leading slashes) against each module's
 * `routePrefix`. Pure and side-effect free so both `moduleRouteGuard` and
 * `AppHostComponent` agree on the same module for the same URL (spec §6.3).
 */
export function resolveModuleForUrl(modules: EnabledModule[], url: string): EnabledModule | undefined {
  const first = url.split('?')[0].split('/').filter(Boolean)[0] ?? '';
  return modules.find(m => m.routePrefix.replace(/^\/+/, '') === first);
}

/**
 * Route guard for the shell's static `**` catch-all child (spec §6.3, amended after the
 * Task 6 review flagged a defect in the original design: `ShellRoutesService` appended
 * routes from `ShellComponent.ngOnInit` via `router.resetConfig`, but the shell only
 * exists AFTER the first navigation has already matched a route — a cold load / refresh
 * of a module URL (e.g. `/hr/employees`) failed to match any route before the dynamic
 * route existed, and there was a race if the user navigated before `/Modules/enabled`
 * returned. A static `**` route gated by this guard closes both holes: the route always
 * exists, and the guard blocks activation until the registry has loaded.
 *
 * On every activation: loads the registry, registers ALL microui prefixes as gated
 * (`PermissionService.registerGatedRoutes`) — even ones with no menu item yet, mirroring
 * the old `ShellRoutesService` behaviour — then resolves which module (if any) owns this
 * URL and defers to `PermissionService.hasAccess`. A URL matching no module, or one the
 * user lacks access to, redirects to `/unauthorized`.
 *
 * `ModuleRegistryService.load()` already fails soft (catches API errors and emits `[]`),
 * so an outage here yields "no module found" → `/unauthorized`, never an unhandled guard
 * error.
 */
export const moduleRouteGuard: CanActivateFn = (route, state) => {
  const registry = inject(ModuleRegistryService);
  const permissions = inject(PermissionService);
  const router = inject(Router);

  return registry.load().pipe(
    switchMap(modules => {
      const microUis = modules.filter(m => m.host === 'microui' && !!m.routePrefix);
      // Fail closed — even prefixes with no matching module resolved below stay gated.
      permissions.registerGatedRoutes(microUis.map(m => m.routePrefix));

      const first = route.url[0]?.path ?? state.url.split('?')[0].split('/').filter(Boolean)[0] ?? '';
      const mod = resolveModuleForUrl(microUis, first);
      if (!mod) return of(router.createUrlTree(['/unauthorized']));

      return permissions.hasAccess(first).pipe(
        map(ok => ok ? true : router.createUrlTree(['/unauthorized']))
      );
    })
  );
};
